/**
 * DarDeBajaDialog (v1.0.26) — flujo cascada para baja de cliente.
 *
 * Flujo:
 * 1. Abrir → call GET /preview-baja → muestra impacto (contratos, facturas, monto)
 * 2. User escribe motivo (mín 3 chars)
 * 3. Confirmar → call POST /dar-de-baja?motivo=...
 * 4. Cierra + invalidate queries del cliente
 */
import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  AlertTriangle,
  Ban,
  FileText,
  Loader2,
  Receipt,
  Wallet,
  X,
  Package,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiCall } from "@/lib/api";
import { formatMXN } from "@/lib/utils";

interface PreviewBaja {
  cliente: { codigo: string; razon_social: string; estatus: string };
  puede_dar_baja: boolean;
  contratos_a_cancelar: number;
  contratos: Array<{
    codigo: string;
    estatus: string;
    tiene_comodato: boolean;
    comodato_pendiente_recoger: boolean;
    pppoe_user: string | null;
  }>;
  facturas_a_anular: number;
  facturas_monto_total: number;
  facturas: Array<{ folio: string; estatus: string; saldo: number }>;
  promesas_a_cancelar: number;
  comodatos_pendientes_recoger: number;
}

export function DarDeBajaDialog({
  clienteId,
  clienteNombre,
  onClose,
  onSuccess,
}: {
  clienteId: string;
  clienteNombre: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const qc = useQueryClient();
  const [motivo, setMotivo] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  const { data: preview, isLoading } = useQuery<PreviewBaja>({
    queryKey: ["cliente-preview-baja", clienteId],
    queryFn: () => apiCall<PreviewBaja>("get", `/api/v1/clientes/${clienteId}/preview-baja`),
  });

  const ejecutar = useMutation({
    mutationFn: async () =>
      apiCall<{ message: string }>(
        "post",
        `/api/v1/clientes/${clienteId}/dar-de-baja?motivo=${encodeURIComponent(motivo.trim())}`
      ),
    onSuccess: (r) => {
      toast.success(r.message);
      qc.invalidateQueries({ queryKey: ["cliente", clienteId] });
      qc.invalidateQueries({ queryKey: ["clientes"] });
      qc.invalidateQueries({ queryKey: ["cliente-resumen", clienteId] });
      onSuccess();
    },
    onError: (e: any) => toast.error(e?.message ?? "Error al dar de baja"),
  });

  const motivoOk = motivo.trim().length >= 3;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-lg border border-border bg-background shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-border p-4">
          <h3 className="flex items-center gap-2 text-sm font-semibold">
            <Ban size={14} className="text-destructive" />
            Dar de baja a {clienteNombre}
          </h3>
          <Button size="sm" variant="ghost" onClick={onClose}>
            <X size={14} />
          </Button>
        </header>

        <div className="space-y-4 p-4">
          {isLoading && <div className="text-xs text-muted-foreground">Cargando preview…</div>}

          {preview && (
            <>
              <div className="rounded-md border border-yellow-300 bg-yellow-50 p-3 text-xs text-yellow-900">
                <AlertTriangle size={12} className="mr-1 inline" />
                <strong>Atención:</strong> esta acción ejecuta una cascada
                irreversible. Los registros NO se borran (histórico fiscal),
                pero el cliente quedará marcado como cancelado.
              </div>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <PreviewCard
                  icon={<FileText size={14} />}
                  label="Contratos"
                  value={preview.contratos_a_cancelar}
                  hint="se cancelarán"
                />
                <PreviewCard
                  icon={<Receipt size={14} />}
                  label="Facturas pendientes"
                  value={preview.facturas_a_anular}
                  hint={`= ${formatMXN(preview.facturas_monto_total)}`}
                />
                <PreviewCard
                  icon={<Wallet size={14} />}
                  label="Promesas vigentes"
                  value={preview.promesas_a_cancelar}
                  hint="se cancelarán"
                />
              </div>

              {preview.comodatos_pendientes_recoger > 0 && (
                <div className="rounded-md border border-orange-300 bg-orange-50 p-2 text-xs text-orange-900">
                  <Package size={12} className="inline align-text-bottom" /> <strong>{preview.comodatos_pendientes_recoger}</strong> comodato(s)
                  pendiente(s) de recoger. Después de la baja, asigna técnico
                  para recuperar equipos.
                </div>
              )}

              {preview.contratos.length > 0 && (
                <details className="rounded-md border border-border p-2 text-xs">
                  <summary className="cursor-pointer font-medium">
                    Detalle ({preview.contratos.length} contrato(s))
                  </summary>
                  <ul className="mt-2 space-y-1 font-mono text-[11px]">
                    {preview.contratos.map((c) => (
                      <li key={c.codigo}>
                        • {c.codigo} ({c.estatus})
                        {c.pppoe_user && <span className="text-muted-foreground"> · PPPoE: {c.pppoe_user}</span>}
                        {c.comodato_pendiente_recoger && <span className="text-orange-700"> · <Package size={11} className="inline align-text-bottom" /></span>}
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              {preview.facturas.length > 0 && (
                <details className="rounded-md border border-border p-2 text-xs">
                  <summary className="cursor-pointer font-medium">
                    Facturas pendientes ({preview.facturas.length})
                  </summary>
                  <ul className="mt-2 space-y-1 font-mono text-[11px]">
                    {preview.facturas.map((f) => (
                      <li key={f.folio}>
                        • {f.folio} · {f.estatus} · {formatMXN(f.saldo)}
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              <div>
                <Label>Motivo de baja (requerido)</Label>
                <Input
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder="ej. Migración a otro ISP, no pago, mudanza..."
                  maxLength={500}
                  autoFocus
                />
                <p className="mt-1 text-[10px] text-muted-foreground">
                  Quedará registrado en audit log y en el campo motivo_baja del cliente.
                </p>
              </div>

              <label className="flex items-start gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  Confirmo que entiendo que esto cancelará{" "}
                  <strong>{preview.contratos_a_cancelar}</strong> contrato(s) +
                  anulará <strong>{preview.facturas_a_anular}</strong> factura(s) por{" "}
                  <strong>{formatMXN(preview.facturas_monto_total)}</strong> y marcará al
                  cliente como cancelado de forma irreversible.
                </span>
              </label>
            </>
          )}
        </div>

        <footer className="flex justify-end gap-2 border-t border-border p-3">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={() => ejecutar.mutate()}
            disabled={!preview || !motivoOk || !confirmed || ejecutar.isPending}
          >
            {ejecutar.isPending && <Loader2 size={12} className="mr-1 animate-spin" />}
            Dar de baja al cliente
          </Button>
        </footer>
      </div>
    </div>
  );
}

function PreviewCard({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  hint: string;
}) {
  return (
    <div className="rounded border border-border bg-muted/30 p-2 text-center">
      <div className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="mt-1 text-lg font-semibold text-foreground">{value}</div>
      <div className="text-[10px] text-muted-foreground">{hint}</div>
    </div>
  );
}
